import { ErrorSidePanel } from '@/components/errors';
import {
  useGetAccountContacts,
  useSendAccountAssociationCode,
  useValidateAccountAssociationContact,
} from '@/hooks/api/account-association.hooks';
import { loadHistoryState } from '@/lib';
import { type AssociationType } from '@/portal-api.definition';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  ErrorCodes,
  logger,
  type NOSIDError,
  templates,
  type SkinPageConfig,
  useHasRequiredFormError,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type AxiosError } from 'axios';
import z from 'zod';
import {
  holderInputStateSchema,
  type ContactSelectionState,
  type CodeInputState,
} from '../-model/flow';
import { type HolderInputProps } from '../-model/props';
import { HolderInputLight } from './-skins';

export const Route = createFileRoute('/(services)/services/_sheet/switch-services/holder-input/')({
  loader: loadHistoryState(holderInputStateSchema, { errorCode: ErrorCodes.AccessDenied }),
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const { associationType } = Route.useLoaderData();
  const navigate = useNavigate();
  const { mutateAsync: identifyContact } = useGetAccountContacts();
  const { mutateAsync: validateContact } = useValidateAccountAssociationContact();
  const { mutateAsync: sendCode, captcha } = useSendAccountAssociationCode();

  const form = useForm(
    {
      associationType: {
        value: associationType,
      },
      value: {
        value: '',
        id: 'holder-input',
        validator: ({ associationType }: { associationType: AssociationType }) => {
          const schema = z.string().min(1, 'Services.AddServices.HolderInput.Errors.ValueRequired');
          switch (associationType) {
            case 'Email':
              return schema.emailAddress('Services.AddServices.HolderInput.Errors.InvalidEmail');
            case 'Phone':
              return schema.phone('Services.AddServices.HolderInput.Errors.InvalidPhone');
            case 'Nif':
              return schema.nif('Services.AddServices.HolderInput.Errors.InvalidNif');
            default:
              return schema;
          }
        },
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  async function handleSubmit({ value }: { value: string }) {
    try {
      const isAssociationWithContactSelection =
        associationType === 'Nif' || associationType === 'Passport';
      if (isAssociationWithContactSelection) {
        const contacts = await identifyContact({ type: associationType, value });
        const state: ContactSelectionState = {
          associationType,
          selectedContact: value,
          contactSelectionContacts: contacts,
        };
        navigate({ to: '/services/switch-services/contact-selection', state });
        return;
      }
      await validateContact({ type: associationType, value });
      if (captcha.challenge && !captcha.token) return;
      await sendCode({ captcha, contactId: undefined, targetId: value, type: associationType });
      const state: CodeInputState = {
        associationType,
        selectedContact: value,
      };
      navigate({ to: '/services/switch-services/code-input', state });
    } catch (err) {
      const errorCode = (err as AxiosError<NOSIDError>).response?.data.ErrorCode;
      if (errorCode === ErrorCodes.PersonNotFound || errorCode === ErrorCodes.UserNotFound)
        form.addError(
          'value',
          `Services.AddServices.HolderInput.Errors.PersonNotFound.${associationType}`,
        );
    }
  }

  const hasRequiredFormError = useHasRequiredFormError(form);

  const props: HolderInputProps = {
    associationType,
    value: form.value.value,
    errors: form.errors.value ?? [],
    inputId: form.ids.value ?? 'value',
    loading: form.loading,
    disabled: hasRequiredFormError,
    onValueChange: (v) => form.setValue('value', v),
    onSubmit: form.handleSubmit(handleSubmit),
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
  };
  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: HolderInputProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <HolderInputLight {...props} />,
  };
}
